import { useTranslation } from "react-i18next";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import { Repeat, Check } from "lucide-react";

const OPTIONS = ["none", "daily", "weekly", "monthly", "yearly"];

const TONE = {
  none: "text-zinc-400 hover:text-zinc-700",
  daily: "text-purple-700",
  weekly: "text-purple-700",
  monthly: "text-purple-700",
  yearly: "text-purple-700",
};

/**
 * Compact inline recurrence picker. Always renders a Repeat icon in the row.
 * Muted when recurrence === 'none', tinted purple otherwise. Clicking opens a
 * popover with the 4 options; selection immediately calls onSelect.
 */
export default function InlineRecurrencePicker({
  recurrence = "none",
  onSelect,
  testId,
  disabled = false,
}) {
  const { t } = useTranslation();
  const current = OPTIONS.includes(recurrence) ? recurrence : "none";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testId}
          onClick={(e) => e.stopPropagation()}
          title={t("tasks.recurrence")}
          className={[
            "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] hover:bg-zinc-100 transition-colors",
            TONE[current],
          ].join(" ")}
        >
          <Repeat className="h-3 w-3" />
          {current !== "none" && (
            <span className="uppercase tracking-wider text-[10px] font-medium">
              {t(`tasks.recurrence_${current}`)}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-48 p-1"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-2 py-1.5 text-[10px] uppercase tracking-[0.15em] font-semibold text-zinc-500">
          {t("tasks.recurrence")}
        </div>
        <ul>
          {OPTIONS.map((opt) => (
            <li key={opt}>
              <button
                type="button"
                onClick={() => onSelect(opt)}
                data-testid={`inline-recurrence-pick-${opt}`}
                className={[
                  "flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm hover:bg-zinc-100 transition-colors",
                  opt === current ? "text-zinc-900 font-medium" : "text-zinc-700",
                ].join(" ")}
              >
                <span>{t(`tasks.recurrence_${opt}`)}</span>
                {opt === current && <Check className="h-3.5 w-3.5" />}
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
